import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils.js'
import { humanise } from '@/lib/format.js'

/**
 * Status badges.
 *
 * ── WHY STATUS COLOUR IS NOT TENANT-DRIVEN ──────────────────────────────────
 * These deliberately use the FIXED status tokens rather than the brand ones.
 * "Paid", "declined" and "overdue" have to mean the same thing in every
 * workspace — if a tenant's brand colour were green, a declined estimate
 * rendered in brand would read as success. Meaning outranks branding here, and
 * the token layers in index.css exist precisely to keep the two separable.
 *
 * ── THE MAPPINGS ARE THE PRODUCT'S OPINION ──────────────────────────────────
 * `sent` is neutral, not positive: an estimate that has been sent has not been
 * won yet, and colouring it green would tell a contractor they have money
 * coming when they have a decision pending. `draft` is quiet on purpose —
 * unfinished work should not compete for attention with real state.
 */

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-sunken text-ink-muted',
        info: 'bg-info-subtle text-info-ink',
        success: 'bg-success-subtle text-success-ink',
        warn: 'bg-warn-subtle text-warn-ink',
        danger: 'bg-danger-subtle text-danger-ink',
        brand: 'bg-brand-subtle text-brand'
      }
    },
    defaultVariants: { tone: 'neutral' }
  }
)

/** Estimate + invoice + job + appointment statuses, in one map. */
const TONES = {
  // estimates
  draft: 'neutral',
  sent: 'info',
  viewed: 'info',
  accepted: 'success',
  declined: 'danger',
  expired: 'warn',

  // invoices
  partial: 'warn',
  paid: 'success',
  overdue: 'danger',
  written_off: 'neutral',
  voided: 'neutral',
  // Phase 2 D24.4. Deliberately NOT the same tone as `voided`, which means
  // something different: voided is "never collected", refunded is
  // "collected and given back". Two similar words, two states, and the
  // dashboard has to keep them apart the same way the CHECK constraint does.
  refunded: 'warn',

  // jobs
  active: 'info',
  won: 'success',
  lost: 'danger',
  on_hold: 'warn',
  cancelled: 'neutral',

  // appointments
  scheduled: 'info',
  confirmed: 'success',
  in_progress: 'brand',
  completed: 'success',
  no_show: 'danger',

  // job priority
  low: 'neutral',
  normal: 'neutral',
  high: 'warn',
  urgent: 'danger'
}

/**
 * @param {object} props
 * @param {string} props.status
 * @param {string} [props.label] override the humanised status text
 */
export function StatusBadge ({ status, label, className }) {
  return (
    <span className={cn(badgeVariants({ tone: TONES[status] ?? 'neutral' }), className)}>
      {label ?? humanise(status)}
    </span>
  )
}

export function Badge ({ tone, className, ...props }) {
  return <span className={cn(badgeVariants({ tone }), className)} {...props} />
}

export { badgeVariants }
